// src/components/Settings/EmailIntegrationForm.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const EmailIntegrationForm = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  // Form state
  const [formState, setFormState] = useState({
    enabled: false,
    email: '',
    password: '',
    imapServer: '',
    imapPort: '993',
    useSSL: true,
    autoScanFrequency: 'daily'
  });
  
  // Load user's existing email integration settings
  useEffect(() => {
    if (user && user.emailIntegration) {
      setFormState({
        enabled: user.emailIntegration.enabled || false,
        email: user.emailIntegration.email || '',
        password: user.emailIntegration.password ? '••••••••' : '',
        imapServer: user.emailIntegration.imapServer || '',
        imapPort: user.emailIntegration.imapPort || '993',
        useSSL: user.emailIntegration.useSSL !== false, // Default to true
        autoScanFrequency: user.emailIntegration.autoScanFrequency || 'daily'
      });
    }
  }, [user]);
  
  // Handle form input changes
  const handleChange = (event) => {
    const { name, value, checked } = event.target;
    setFormState(prevState => ({
      ...prevState,
      [name]: name === 'enabled' || name === 'useSSL' ? checked : value
    }));
  };
  
  // Common email providers and their IMAP settings
  const commonProviders = {
    'Gmail': {
      imapServer: 'imap.gmail.com',
      imapPort: '993',
      useSSL: true,
      note: 'For Gmail, you need to enable "Less secure app access" or use an App Password.'
    },
    'Outlook/Hotmail': {
      imapServer: 'outlook.office365.com',
      imapPort: '993',
      useSSL: true,
      note: 'For Outlook, you might need to enable IMAP access in your account settings.'
    },
    'Yahoo': {
      imapServer: 'imap.mail.yahoo.com',
      imapPort: '993',
      useSSL: true,
      note: 'For Yahoo, you need to create an App Password in your account security settings.'
    },
    'AOL': {
      imapServer: 'imap.aol.com',
      imapPort: '993',
      useSSL: true,
      note: null
    },
    'iCloud': {
      imapServer: 'imap.mail.me.com',
      imapPort: '993',
      useSSL: true,
      note: 'For iCloud, you need to generate an App-Specific Password.'
    }
  };
  
  // Auto-detect email provider
  const detectProvider = () => {
    const email = formState.email.toLowerCase();
    
    if (email.includes('gmail') || email.includes('googlemail')) {
      return 'Gmail';
    } else if (email.includes('outlook') || email.includes('hotmail') || email.includes('live')) {
      return 'Outlook/Hotmail';
    } else if (email.includes('yahoo')) {
      return 'Yahoo';
    } else if (email.includes('aol')) {
      return 'AOL';
    } else if (email.includes('icloud') || email.includes('me.com') || email.includes('mac.com')) {
      return 'iCloud';
    }
    
    return null;
  };
  
  // Apply settings for a provider
  const applyProviderSettings = (provider) => {
    if (commonProviders[provider]) {
      const settings = commonProviders[provider];
      setFormState(prevState => ({
        ...prevState,
        imapServer: settings.imapServer,
        imapPort: settings.imapPort,
        useSSL: settings.useSSL
      }));
      
      // Show provider-specific note if available
      if (settings.note) {
        setError(null);
        setSuccess(settings.note);
      }
    }
  };
  
  // Handle auto-detect button click
  const handleAutoDetect = () => {
    const provider = detectProvider();
    if (provider) {
      applyProviderSettings(provider);
    } else {
      setError('Could not auto-detect email provider. Please enter server details manually.');
      setSuccess(null);
    }
  };
  
  // Test connection to email server
  const testConnection = async () => {
    // Don't test if required fields are missing
    if (!formState.email || !formState.password || !formState.imapServer) {
      setError('Please fill in all required fields before testing connection.');
      setSuccess(null);
      return;
    }
    
    setTesting(true);
    setError(null);
    setSuccess(null);
    
    try {
      const response = await fetch('/api/settings/email-integration/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          email: formState.email,
          password: formState.password === '••••••••' ? null : formState.password,
          imapServer: formState.imapServer,
          imapPort: formState.imapPort,
          useSSL: formState.useSSL
        })
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        setSuccess('Connection successful! Email integration is working properly.');
      } else {
        setError(`Connection failed: ${result.error || 'Unknown error'}`);
      }
    } catch (err) {
      setError(`Connection test failed: ${err.message}`);
    } finally {
      setTesting(false);
    }
  };
  
  // Save email integration settings
  const saveSettings = async () => {
    // Validate form
    if (formState.enabled && (!formState.email || !formState.password || !formState.imapServer)) {
      setError('Please fill in all required fields.');
      setSuccess(null);
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const response = await fetch('/api/settings/email-integration', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          enabled: formState.enabled,
          email: formState.email,
          password: formState.password === '••••••••' ? null : formState.password,
          imapServer: formState.imapServer,
          imapPort: formState.imapPort,
          useSSL: formState.useSSL,
          autoScanFrequency: formState.autoScanFrequency
        })
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setSuccess('Email integration settings saved successfully.');
      } else {
        setError(`Failed to save settings: ${result.error || 'Unknown error'}`);
      }
    } catch (err) {
      setError(`Error saving settings: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Manual scan for receipts
  const scanNow = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const response = await fetch('/api/receipts/scan-email', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        setSuccess(`Scan completed! Found ${result.receiptsFound} receipt(s).`);
      } else {
        setError(`Scan failed: ${result.error || 'Unknown error'}`);
      }
    } catch (err) {
      setError(`Error during scan: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Card>
      <CardHeader 
        title="Email Receipt Integration" 
        subheader="Connect your email to automatically import digital receipts"
      />
      <Divider />
      <CardContent>
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={formState.enabled}
                onChange={handleChange}
                name="enabled"
                color="primary"
              />
            }
            label="Enable Email Receipt Integration"
          />
          <Typography variant="body2" color="text.secondary">
            When enabled, we'll scan your email for digital receipts and automatically import them.
          </Typography>
        </Box>
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              value={formState.email}
              onChange={handleChange}
              disabled={!formState.enabled || loading}
              required
              variant="outlined"
              margin="normal"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formState.password}
              onChange={handleChange}
              disabled={!formState.enabled || loading}
              required
              variant="outlined"
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <FormHelperText>
              Your password is securely encrypted and only used to access your email for receipts.
            </FormHelperText>
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                Email Server Settings
              </Typography>
              <Button 
                variant="outlined" 
                size="small"
                onClick={handleAutoDetect}
                disabled={!formState.email || !formState.enabled || loading}
              >
                Auto-Detect
              </Button>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="IMAP Server"
              name="imapServer"
              value={formState.imapServer}
              onChange={handleChange}
              disabled={!formState.enabled || loading}
              required
              variant="outlined"
              placeholder="e.g., imap.gmail.com"
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Port"
              name="imapPort"
              value={formState.imapPort}
              onChange={handleChange}
              disabled={!formState.enabled || loading}
              variant="outlined"
              type="number"
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={formState.useSSL}
                  onChange={handleChange}
                  name="useSSL"
                  color="primary"
                  disabled={!formState.enabled || loading}
                />
              }
              label="Use SSL"
            />
          </Grid>
          
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              Scanning Options
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Auto-Scan Frequency"
              name="autoScanFrequency"
              value={formState.autoScanFrequency}
              onChange={handleChange}
              disabled={!formState.enabled || loading}
              variant="outlined"
              SelectProps={{
                native: true
              }}
            >
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="manual">Manual Only</option>
            </TextField>
            <FormHelperText>
              How often should we automatically scan your email for new receipts?
            </FormHelperText>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Button
                variant="outlined"
                onClick={scanNow}
                disabled={!formState.enabled || loading || !user?.emailIntegration?.enabled}
                startIcon={loading ? <CircularProgress size={20} /> : null}
                sx={{ mt: 1 }}
              >
                Scan Email Now
              </Button>
            </Box>
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            onClick={testConnection}
            disabled={!formState.enabled || !formState.email || !formState.password || !formState.imapServer || testing || loading}
            startIcon={testing ? <CircularProgress size={20} /> : null}
          >
            Test Connection
          </Button>
          
          <Button
            variant="contained"
            onClick={saveSettings}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Save Settings
          </Button>
        </Box>
        
        <Box sx={{ mt: 4, bgcolor: 'background.default', p: 2, borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <InfoIcon color="info" sx={{ mr: 1 }} />
            <Typography variant="subtitle2">Important Privacy Information</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Your email credentials are encrypted and securely stored. We only access emails that appear to be receipts
            and never read your personal messages. You can disable this integration at any time.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EmailIntegrationForm;